/** Overview report tab showing report metadata */
import {
  IonAccordion,
  IonAccordionGroup,
  IonAlert,
  IonButton,
  IonCard,
  IonCardContent,
  IonChip,
  IonCol,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { addCircleOutline, chevronDownCircleOutline, closeCircleOutline } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/Button';
import { AddClientModalContent } from '@/components/Clients/AddClientModalContent';
import { CreateSessionModalContent } from '@/components/GOSession/CreateSessionModalContent';
import { Modal } from '@/components/Modal/Modal';
import { ORYXCardHeader } from '@/components/SectionHeader';
import { Toast } from '@/components/Toasts/Toast';

import UserService from '@/hooks/useClients/userService';

import { useAuthContext } from '@/contexts/authContext';
import { useReportContext } from '@/contexts/reportContext';

import ORYXEditIcon from '@/assets/icons/ORYX_EditIcon.svg';

import { FirebaseTimestamp, ReportType } from '@/models';
import { FileInfo } from '@/models/reportV2.model';

type OverviewReportProps = {
  id: string;
  fileInfo: FileInfo;
  reportType: ReportType;
};
export const getActivity = (reportType: ReportType | undefined) => {
  switch (reportType) {
    case 1:
      return 'Walk';
    case 2:
      return 'Run';
    case 3:
      return 'Squat';
    case 4:
      return 'Single Leg Squat';
    default:
      return 'Error';
  }
};

function convertDate(timestamp: FirebaseTimestamp) {
  const datestring = timestamp.toDate().toLocaleString();
  return datestring;
}

/** Overview report tab showing report metadata
 * @param report - The full report data, including reportInfo data
 */
export const OverviewReportTab = ({ id, fileInfo, reportType }: OverviewReportProps) => {
  const [isTouched, setIsTouched] = useState(false);
  const [addTags, setAddTags] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    editTags,
    isCompleted,
    isError,
    clearError,
    tags,
    fileListener,
    reportName,
    editReportName,
    notes,
    addReportNotes,
    editReportNotes,
  } = useReportContext();
  const [editName, setEditName] = useState(false);
  const [loadingReportName, setLoadingReportName] = useState(false);
  const [addNote, setAddNote] = useState(false);
  const [editNote, setEditNote] = useState<{ show: boolean; note: number | undefined; index: number }>({
    show: false,
    note: undefined,
    index: 0,
  });

  // if (import.meta.env.MODE === 'development') console.log('OverviewReportTab: data', data);
  const activity = getActivity(reportType);

  const uploadDate = new Date(fileInfo.uploadTimestamp * 1000);
  const uploadDateString = uploadDate?.toLocaleString();
  useEffect(() => {
    if (fileInfo) {
      fileListener(id);
    }
  }, [fileInfo]);

  useEffect(() => {
    console.log('Notes! ', notes);
  }, [notes]);

  // #region Tags
  const handleInputTags = (ev: Event) => {
    // If no comma is found, use the string as a single tag in the array
    // if a comma is found, split the string into an array of tags
    const input = (ev.target as HTMLInputElement).value;
    if (input.includes(', ')) {
      localStorage.setItem('tags', JSON.stringify([input]));
    } else {
      localStorage.setItem('tags', JSON.stringify([input]));
    }
  };

  // function triggered when user leaves the Tag input field
  const handleAddTags = () => {
    setLoading(true);
    const tagsLocalStorage = localStorage.getItem('tags');
    if (tagsLocalStorage) {
      // create array from the local storage string
      const tagsArray = JSON.parse(tagsLocalStorage!);
      const tagsToUpload = tagsArray[0].split(', ');
      setTimeout(() => {
        editTags(id, tagsToUpload);
        setAddTags(false);
        setIsTouched(false);
        localStorage.removeItem('tags');
        setLoading(false);
      }, 1000);
    }
  };

  // handle tags deletion
  const handleTagDelete = (index: number) => {
    // remove the tag from the array and update the db
    // if there is only 1 tag in the array, replace the entry with a blank string
    if (tags.length === 1) {
      editTags(id, ['']);
    }
    // if there are more than 1 tags in the array, remove the tag at the index
    else {
      const newTags = tags.filter((tag, i) => i !== index);
      editTags(id, newTags);
    }
  };

  // #endregion

  // #region Report Name
  const handleInputReportName = (ev: Event) => {
    const input = (ev.target as HTMLInputElement).value;
    localStorage.setItem('reportName', input);
  };

  const handleAddReportName = () => {
    setLoadingReportName(true);
    const reportNameLocalStorage = localStorage.getItem('reportName');
    if (reportNameLocalStorage) {
      setTimeout(() => {
        editReportName(id, reportNameLocalStorage);
        localStorage.removeItem('reportName');
        setLoadingReportName(false);
        setEditName(false);
      }, 1000);
    }
  };

  // #endregion

  // #region Note
  const accordionGroup = useRef<null | HTMLIonAccordionGroupElement>(null);

  useEffect(() => {
    if (!accordionGroup.current) {
      return;
    }
    // Set the IDs of the accordion items you want to open (e.g., 'accordion-0' and 'accordion-2')
    accordionGroup.current.value = ['accordion-0'];
  }, []);
  // #endregion
  useEffect(() => {
    if (import.meta.env.MODE) console.log('db Tags', tags);
    if (import.meta.env.MODE) console.log('db reportName', reportName);
  }, [tags, reportName]);

  // Client Specific code
  const user = useAuthContext().state.userState?.user;
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [clientData, setClientData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: new Date().toISOString(),
    email: '',
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  // Handle input change and reset errors when the user types
  const handleInputChange = (field: string, value: string) => {
    setClientData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' })); // Reset error message
  };

  // Validation function
  const validateForm = () => {
    const newErrors = { firstName: '', lastName: '', email: '' };
    let isValid = true;

    if (!clientData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      isValid = false;
    }
    if (!clientData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      isValid = false;
    }
    if (!clientData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(clientData.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle Confirm button
  const handleConfirm = async () => {
    if (validateForm()) {
      setCreatingClient(true);
      console.log('Client Data:', clientData);
      try {
        await UserService.addClient(clientData);
        console.log('Client added successfully');
        setIsAddClientModalOpen(false);
        setClientData({ firstName: '', lastName: '', dateOfBirth: new Date().toISOString(), email: '' });
        setSuccess('Client added successfully');
      } catch (error: any) {
        if (error.message === 'A user with this email already exists.') {
          setErrors((prev) => ({ ...prev, email: error.message }));
        } else {
          setError(error.message);
        }
      } finally {
        setCreatingClient(false);
      }
    }
  };

  // Session data
  const [isAddSessionModalOpen, setIsAddSessionModalOpen] = useState(false);
  const [sessionID, setSessionID] = useState('');
  const [creatingSession, setCreatingSession] = useState(false);
  const [sessionData, setSessionData] = useState({
    date: new Date().toISOString(),
    clientId: '',
    therapistId: user!.uid,
  });

  const handleSessionInputChange = (field: string, value: string) => {
    setSessionData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSessionConfirm = async () => {
    setCreatingSession(true);
    console.log('Session Data:', sessionData);
    try {
      setSessionID(await UserService.createSession(sessionData));
      console.log('Session Created successfully');
      setIsAddSessionModalOpen(false);
      setSessionData({ date: new Date().toISOString(), clientId: '', therapistId: user!.uid });
      setSuccess('Session added successfully');
    } catch (error: any) {
      console.log('Error creating session', error);
    } finally {
      setCreatingSession(false);
    }
  };

  useEffect(() => {
    console.log('Session ID:', sessionID);
    if (sessionID) {
      setTimeout(() => {
        UserService.moveFileToReports(user!.uid, sessionID, id);
      }, 1000);
    }
  }, [sessionID]);

  if (!fileInfo) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      <Modal
        footer={
          <>
            <Button onClick={() => setIsAddSessionModalOpen(false)} variant='secondary'>
              Cancel
            </Button>
            <Button onClick={() => handleSessionConfirm()} variant='primary'>
              Confirm
            </Button>
          </>
        }
        header='Add new session'
        isOpen={isAddSessionModalOpen}
        onDidDismiss={() => setIsAddSessionModalOpen(false)}>
        <CreateSessionModalContent
          loading={creatingSession}
          onInputChange={handleSessionInputChange}
          sessionData={sessionData}
        />
      </Modal>
      <Modal
        footer={
          <>
            <Button onClick={() => setIsAddClientModalOpen(false)} variant='secondary'>
              Cancel
            </Button>
            <Button onClick={() => handleConfirm()} variant='primary'>
              Confirm
            </Button>
          </>
        }
        header='Add new client'
        isOpen={isAddClientModalOpen}
        onDidDismiss={() => setIsAddClientModalOpen(false)}>
        <AddClientModalContent
          clientData={clientData}
          errors={errors}
          loading={creatingClient}
          onInputChange={handleInputChange}
        />
      </Modal>
      <Toast isOpen={isCompleted} message={'File updated successfully!'} type={'info'} />

      <IonAlert
        buttons={[
          { text: 'Cancel', role: 'cancel', cssClass: 'destructiveAlertButton', handler: () => setAddNote(false) },
          { text: 'Save', handler: (alertData) => addReportNotes(id, alertData.note) },
        ]}
        className='informativeAlert'
        header='Please enter your note'
        inputs={[
          {
            name: 'note',
            type: 'textarea',
            placeholder: 'Add note here...',
          },
        ]}
        isOpen={addNote}
        onDidDismiss={() => setAddNote(false)}
        trigger='add-note'
      />

      <IonAlert
        buttons={[
          { text: 'Cancel', role: 'cancel', cssClass: 'destructiveAlertButton' },
          { text: 'Save', handler: (alertData) => editReportNotes(id, alertData.note, editNote.index) },
        ]}
        className='informativeAlert'
        header='Please edit your note'
        inputs={[
          {
            name: 'note',
            type: 'textarea',
            placeholder: `${editNote.note}`,
          },
        ]}
        isOpen={editNote.show}
      />

      <IonRow className=''>
        <IonCol size='6'>
          {/* file Info */}
          <IonCard style={{ height: '100%' }}>
            <ORYXCardHeader title={'File info'} />
            <IonCardContent className='reportcardContent'>
              <IonRow className='ion-align-items-center'>
                <IonCol className='' size='4'>
                  <strong>Upload date: </strong>
                </IonCol>
                <IonCol className='' size='8'>
                  {uploadDateString}
                </IonCol>
              </IonRow>

              <IonRow className='ion-align-items-center'>
                <IonCol size='4'>
                  <strong>Filename: </strong>
                </IonCol>
                <IonCol size='8'>{fileInfo.filename}</IonCol>
              </IonRow>

              <IonRow className='ion-align-items-center'>
                <IonCol size='4'>
                  <strong>Activity: </strong>
                </IonCol>
                <IonCol size='8'>{activity}</IonCol>
              </IonRow>

              <IonRow className='ion-align-items-center'>
                <IonCol size='4'>
                  <strong>Reportname: </strong>
                </IonCol>
                {!editName ? (
                  <>
                    <IonCol size='7'>{reportName}</IonCol>
                    <IonCol className='ion-text-center ion-align-items-center' size='1'>
                      <IonIcon
                        color='primary'
                        icon={ORYXEditIcon}
                        onClick={() => setEditName(true)}
                        style={{ display: 'block', margin: 'auto' }}
                      />
                    </IonCol>{' '}
                  </>
                ) : (
                  <>
                    <IonCol size='7'>
                      <IonInput
                        autocapitalize='on'
                        className='create_ListInput'
                        clearInput={true}
                        fill='outline'
                        mode='ios'
                        onIonBlur={() => setEditName(false)}
                        onIonInput={(e) => {
                          handleInputReportName(e);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAddReportName();
                            setEditName(false);
                          }
                        }}
                        placeholder='Enter (new) reportname'
                        spellCheck={false}
                        type='text'
                      />
                    </IonCol>

                    {loadingReportName && (
                      <IonCol className='ion-text-center ion-align-items-center Test' size='1'>
                        <IonSpinner className='' color='primary' name='bubbles' />
                      </IonCol>
                    )}
                  </>
                )}
              </IonRow>
            </IonCardContent>
          </IonCard>
        </IonCol>

        <IonCol size='6'>
          {/* Tags */}
          <IonCard style={{ height: '100%' }}>
            <ORYXCardHeader title={'Tags'}>
              <IonButton fill='clear' icon-only onClick={() => setAddTags(true)}>
                <IonIcon src={addCircleOutline} />
              </IonButton>
            </ORYXCardHeader>
            <IonCardContent className='reportcardContent'>
              <IonRow className='ion-align-items-center'>
                <IonCol className='ion-text-center' size='12'>
                  {tags[0] != '' ? (
                    <>
                      {tags.map((tag: any, index: any) => (
                        <IonChip key={tag}>
                          {tag}
                          <IonIcon
                            color='warning'
                            icon={closeCircleOutline}
                            onClick={() => handleTagDelete(index)}></IonIcon>
                        </IonChip>
                      ))}
                    </>
                  ) : (
                    <>
                      <IonItem className='' lines='none'>
                        <IonLabel className=''>No tags found</IonLabel>
                      </IonItem>
                    </>
                  )}
                </IonCol>
              </IonRow>
              <IonList lines='none'>
                {addTags && (
                  <>
                    <IonItem>
                      <IonInput
                        autocapitalize='off'
                        className='ORYX_Input'
                        clearInput={true}
                        helperText='Seperate multiple tags by a comma'
                        inputMode='text'
                        label='Tags'
                        labelPlacement='stacked'
                        mode='ios'
                        onIonBlur={() => setAddTags(false)}
                        onIonInput={(e) => {
                          handleInputTags(e);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAddTags();
                        }}
                        spellCheck={false}
                        type='text'
                      />
                    </IonItem>
                    <IonRow className='ion-justify-content-between ion-align-items-center'>
                      <IonCol className='ion-padding-horizontal' size='11'>
                        {/* <IonButton onClick={handleAddTags} expand='block' fill='outline' color='primary' /> */}
                      </IonCol>

                      <IonCol className='ion-no-padding ion-text-center' size='1'>
                        {loading ? (
                          <IonSpinner className='' color='primary' name='bubbles' />
                        ) : (
                          <IonIcon color='warning' onClick={() => setAddTags(false)} src={closeCircleOutline} />
                        )}
                      </IonCol>
                    </IonRow>
                  </>
                )}
              </IonList>
            </IonCardContent>
          </IonCard>
        </IonCol>
      </IonRow>
      <IonRow className='ion-margin-bottom'>
        <IonCol size='12'>
          {/* Notes */}
          <IonCard style={{ height: '100%', marginBottom: '20px' }}>
            <ORYXCardHeader title={'Notes'}>
              <IonButton fill='clear' icon-only id='add-note' onClick={() => setAddNote(true)}>
                <IonIcon src={addCircleOutline} />
              </IonButton>
            </ORYXCardHeader>
            <IonCardContent className='reportcardContent'>
              <IonRow>
                <IonCol size='12'>
                  {notes[0] == '' ? (
                    <IonText>No notes found</IonText>
                  ) : (
                    <>
                      <IonAccordionGroup expand='inset' mode='md' multiple={true} ref={accordionGroup}>
                        {notes.reverse().map((note: any, index: any) => (
                          <IonAccordion key={index} toggleIcon={chevronDownCircleOutline} value={`accordion-${index}`}>
                            <IonItem className='ion_accordion_item' slot='header'>
                              <IonText>{convertDate(note.createdOn)}</IonText>
                            </IonItem>
                            <IonRow className='ion-padding ion-align-items-center' slot='content'>
                              <IonCol size='11'>{note.note}</IonCol>
                            </IonRow>
                          </IonAccordion>
                        ))}
                      </IonAccordionGroup>
                    </>
                  )}
                </IonCol>
              </IonRow>
            </IonCardContent>
          </IonCard>
        </IonCol>
      </IonRow>
      <IonRow className='Test ion-margin-top'>
        <IonCol size='auto'>
          <Button leadingIcon='plus' onClick={() => setIsAddClientModalOpen(true)} size='medium' variant='primary'>
            Add Client
          </Button>
        </IonCol>
        <IonCol size='auto'>
          <Button leadingIcon='plus' onClick={() => setIsAddSessionModalOpen(true)} size='medium' variant='primary'>
            Create Session
          </Button>
        </IonCol>
      </IonRow>
    </>
  );
};
